import { useEffect } from 'react'

/**
 * UID-335 — re-run `refetch` whenever the tab regains visibility (switching
 * back from another tab/app, or waking from sleep). Exists so a background
 * write that lands WHILE the tab is elsewhere (e.g. UID-330's async thumbnail
 * generation) shows up without a manual reload — `UploadsPage` (over
 * `useUploadSessions`'s `refetch`) and `usePartAttachments` both already
 * poll nothing and rely on a one-shot fetch-on-mount plus a manual
 * `refetch()` trigger.
 *
 * CALL THIS AT THE PAGE/COMPONENT LEVEL, not inside a widely-shared data
 * hook: `useUploadSessions` itself is also called from the globally-mounted
 * `Sidebar` (its in-progress badge count) — wiring this hook into
 * `useUploadSessions` directly would fire a `listSessions()` network call on
 * every tab-focus on every authenticated page, not just the Uploads page.
 *
 * Uses `visibilitychange` rather than `window`'s `focus` event: `focus` also
 * fires on trivial same-tab focus changes (e.g. clicking a `<select>`) that
 * have nothing to do with the tab having been away, which would refetch far
 * more often than the actual "user came back to this tab" moment this hook
 * targets. `visibilitychange` fires only on a genuine hidden→visible
 * transition, including tab-switch, window-minimize/restore, and OS sleep.
 *
 * `refetch` is expected to be a referentially STABLE callback (both current
 * call sites' `refetch` is a `useCallback` wrapping a `setState` updater) —
 * an unstable one would tear the listener down and rebuild it on every
 * render, which is wasted work but not a correctness bug (the listener is
 * still attached whenever this effect runs).
 */
export function useRefetchOnWindowFocus(refetch: () => void): void {
  useEffect(() => {
    function handleVisibilityChange(): void {
      if (document.visibilityState === 'visible') {
        refetch()
      }
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [refetch])
}

/**
 * UID-335 follow-up — `useRefetchAfterDelay`'s single fixed-delay check is a
 * GUESS about how long client-side thumbnail generation takes, and that guess
 * was measured before this renderer grew a heavier pipeline (512px canvas,
 * 2048×2048 shadow maps, a PMREM environment bake, occt WASM tessellation for
 * STEP/IGES) — reported as "In Progress" rows on the Uploads LIST never
 * picking up a thumbnail that finishes generating after the one-shot window,
 * while the detail page (a fresh fetch, issued later) already has it.
 *
 * This polls every `intervalMs` while `active` is true, and stops itself
 * (clears the interval) once either `active` goes false — the caller passes
 * "is there still an in-progress session with fewer thumbnails than files" —
 * or `maxDurationMs` elapses, so a session that never finishes generating
 * (e.g. a render failure) cannot poll forever.
 */
export function usePollWhilePending(
  refetch: () => void,
  active: boolean,
  intervalMs: number,
  maxDurationMs: number,
): void {
  useEffect(() => {
    if (!active) return
    let deadline = Date.now() + maxDurationMs
    const timer = setInterval(() => {
      // UID-589 (Task 7) — a HIDDEN tab neither refetches nor spends the
      // budget.
      //
      // Skipping the refetch is the obvious half: `setInterval` is only
      // THROTTLED in a background tab, not paused, so this kept firing a full
      // `GET /uploads/sessions` (every session, every fileId, every presigned
      // URL) at a tab nobody was looking at. `useRefetchOnWindowFocus` above
      // already refetches on the hidden→visible transition, so the
      // come-back case was covered twice over and nothing is lost by
      // dropping these ticks.
      //
      // Pushing `deadline` out is the half that MUST come with it. The ceiling
      // exists so a render that never finishes can't poll forever — it is a
      // bound on ATTEMPTS, not on wall-clock time. Left alone, a user who
      // uploaded and tabbed away for 20 s would return to a poll whose window
      // had already burned down to nothing having issued zero useful requests,
      // and it could not restart: the interval is cleared at the deadline while
      // `active` is still true, so the effect never re-runs. Deferring the
      // deadline by exactly one interval per skipped tick keeps the number of
      // VISIBLE attempts identical to before this change — which is what the
      // `maxDurationMs` cases in this file's tests pin.
      if (document.visibilityState !== 'visible') {
        deadline += intervalMs
        return
      }
      if (Date.now() >= deadline) {
        clearInterval(timer)
        return
      }
      refetch()
    }, intervalMs)
    return () => clearInterval(timer)
  }, [active, refetch, intervalMs, maxDurationMs])
}
